// Markdown renderer component for post detail view
// Uses react-markdown for safe and proper markdown rendering

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import type { Components } from 'react-markdown'
import { getProxiedImageUrl } from '@/utils/imageProxy'

interface MarkdownRendererProps {
  content: string
  className?: string
}

// Regex patterns for image URL detection
const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i
const STEEMIT_IMAGES_URL = /^https?:\/\/steemitimages\.com\//i
const IPFS_URL = /^https?:\/\/ipfs\./i

// Regex patterns for YouTube URL detection
const YOUTUBE_PATTERNS = [
  /^https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/i,
  /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/i,
  /^https?:\/\/youtu\.be\/([a-zA-Z0-9_-]+)/i,
  /^https?:\/\/(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/i,
]

// Check if a URL is an image URL
function isImageUrl(url: string): boolean {
  return IMAGE_EXTENSIONS.test(url) || STEEMIT_IMAGES_URL.test(url) || IPFS_URL.test(url)
}

// Extract YouTube video ID from URL
function extractYouTubeVideoId(url: string): string | null {
  for (const pattern of YOUTUBE_PATTERNS) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return match[1]
    }
  }
  return null
}

// Convert YouTube URL to embed iframe
function youtubeUrlToEmbed(url: string): string | null {
  const videoId = extractYouTubeVideoId(url)
  if (videoId) {
    return `<iframe src="https://www.youtube.com/embed/${videoId}" title="YouTube video" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`
  }
  return null
}

// Preprocess content to convert standalone image URLs to img tags and YouTube URLs to iframes
function preprocessContent(content: string): string {
  // Split content by lines to process each line
  const lines = content.split('\n')

  const processedLines = lines.map(line => {
    const trimmedLine = line.trim()

    // Skip if line is already markdown image syntax, HTML img tag, or iframe
    if (trimmedLine.startsWith('![') || trimmedLine.startsWith('<img') || trimmedLine.startsWith('<iframe')) {
      return line
    }

    // Check if the line is a standalone URL (possibly with http:// or https://)
    const urlMatch = trimmedLine.match(/^(https?:\/\/[^\s<>]+)$/i)
    if (urlMatch && urlMatch[1]) {
      const url = urlMatch[1]

      // Check for YouTube URL first
      const youtubeEmbed = youtubeUrlToEmbed(url)
      if (youtubeEmbed) {
        return youtubeEmbed
      }

      // If it looks like an image URL, convert it to an img tag
      if (isImageUrl(url)) {
        return `<img src="${url}" alt="" />`
      }
    }

    return line
  })

  return processedLines.join('\n')
}

// Custom components for react-markdown
const components: Components = {
  // Apply image proxy and styling to images
  img: ({ src, alt, ...props }) => {
    const proxiedSrc = src ? getProxiedImageUrl(src, 0, 0) : ''
    return (
      <img
        src={proxiedSrc}
        alt={alt || ''}
        className="max-w-full rounded-lg my-4"
        loading="lazy"
        {...props}
      />
    )
  },
  // Open external links in new tab
  a: ({ href, children, ...props }) => {
    const isExternal = href?.startsWith('http')
    return (
      <a
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        className="text-primary hover:underline"
        {...props}
      >
        {children}
      </a>
    )
  },
  // Style code blocks
  pre: ({ children, ...props }) => (
    <pre
      className="overflow-x-auto rounded-lg bg-muted p-4 my-4"
      {...props}
    >
      {children}
    </pre>
  ),
  // Style inline code
  code: ({ children, ...props }) => {
    // Check if this is inline code (no className with language)
    const isInline = !props.className
    if (isInline) {
      return (
        <code
          className="rounded bg-muted px-1.5 py-0.5 text-sm"
          {...props}
        >
          {children}
        </code>
      )
    }
    return <code {...props}>{children}</code>
  },
  // Style blockquotes
  blockquote: ({ children, ...props }) => (
    <blockquote
      className="border-l-4 border-muted-foreground/30 pl-4 italic my-4"
      {...props}
    >
      {children}
    </blockquote>
  ),
  // Style tables
  table: ({ children, ...props }) => (
    <div className="overflow-x-auto my-4">
      <table className="min-w-full border-collapse" {...props}>
        {children}
      </table>
    </div>
  ),
  th: ({ children, ...props }) => (
    <th
      className="border border-border bg-muted px-4 py-2 text-left font-semibold"
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td className="border border-border px-4 py-2" {...props}>
      {children}
    </td>
  ),
  // Style headings
  h1: ({ children, ...props }) => (
    <h1 className="text-3xl font-bold mt-8 mb-4" {...props}>
      {children}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 className="text-2xl font-bold mt-6 mb-3" {...props}>
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 className="text-xl font-bold mt-5 mb-2" {...props}>
      {children}
    </h3>
  ),
  h4: ({ children, ...props }) => (
    <h4 className="text-lg font-semibold mt-4 mb-2" {...props}>
      {children}
    </h4>
  ),
  // Style lists
  ul: ({ children, ...props }) => (
    <ul className="list-disc pl-6 my-4 space-y-1" {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="list-decimal pl-6 my-4 space-y-1" {...props}>
      {children}
    </ol>
  ),
  // Style paragraphs
  p: ({ children, ...props }) => (
    <p className="my-4 leading-relaxed" {...props}>
      {children}
    </p>
  ),
  // Style horizontal rules
  hr: ({ ...props }) => (
    <hr className="my-8 border-border" {...props} />
  ),
  // Make iframes responsive (especially for YouTube embeds)
  iframe: ({ src, title, ...props }) => (
    <div className="relative my-4 w-full overflow-hidden rounded-lg aspect-video">
      <iframe
        src={src}
        title={title || 'Embedded content'}
        className="absolute top-0 left-0 w-full h-full"
        allowFullScreen
        {...props}
      />
    </div>
  ),
}

export function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
  // Preprocess content to convert standalone image URLs to img tags
  const processedContent = preprocessContent(content)

  return (
    <div className={`markdown-content ${className}`.trim()}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={components}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  )
}
